import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import type { Company, Group, IndustryType, Unit, UnitFilterData } from '@/types';
import { getCompanies, getGroupsForCompany, getBulkGroupsForCompanies, getUnits } from '@/lib/api';
import {
  CACHE_KEY_BASE,
  CACHE_DURATION_MS,
  validateAndRestoreIndustryTypes,
  getCachedData,
  setCachedData,
  mergeSelectionWithNewEntities,
} from '../FilterContext';

interface FleetDataLoadersArgs {
  user: { id: string } | null | undefined;
  rbacReady: boolean;
  companies: Company[];
  selectedCompanyIds: string[];
  hasInitialLoad: boolean;
  refreshNonce: number;
  setCompanies: Dispatch<SetStateAction<Company[]>>;
  setGroups: Dispatch<SetStateAction<Group[]>>;
  setUnitsInternal: Dispatch<SetStateAction<(Unit | UnitFilterData)[]>>;
  setSelectedCompanyIds: Dispatch<SetStateAction<string[]>>;
  setSelectedGroupIds: Dispatch<SetStateAction<string[]>>;
  setSelectedIndustryTypes: Dispatch<SetStateAction<IndustryType[]>>;
  setHasInitialLoad: Dispatch<SetStateAction<boolean>>;
}

/**
 * The three fleet-structure loader effects (companies → groups → units),
 * extracted verbatim from FilterProvider (ISS-030). MUST be called AFTER
 * useFilterCacheRestore and BEFORE useFilterSelectionSync so effect-registration
 * order — and the loaders' synchronous cache reads relative to the sync writes —
 * is unchanged. Stable useState setters are listed in the dependency arrays
 * (they never change identity) purely to keep the arrays honest; `companies` is
 * intentionally depended on by `.length` only, exactly as before.
 */
export function useFleetDataLoaders({
  user,
  rbacReady,
  companies,
  selectedCompanyIds,
  hasInitialLoad,
  refreshNonce,
  setCompanies,
  setGroups,
  setUnitsInternal,
  setSelectedCompanyIds,
  setSelectedGroupIds,
  setSelectedIndustryTypes,
  setHasInitialLoad,
}: FleetDataLoadersArgs) {
  // Fetch companies only when cache is stale or empty
  // CRITICAL: Must wait for rbacReady to prevent unauthorized data exposure
  useEffect(() => {
    // CRITICAL: Wait for RBAC to be ready - this is the key guard against the flash
    if (!rbacReady) {
      console.log('⏳ [FilterContext] Companies: waiting for rbacReady');
      return;
    }

    // If no user, clear data (shouldn't happen if rbacReady, but defensive)
    if (!user?.id) {
      console.log('⚠️ [FilterContext] No authenticated user');
      return;
    }

    const userId = user.id;

    // Check cache validity with user-specific key
    const cacheTimestamp = getCachedData<string>(CACHE_KEY_BASE.timestamp, userId);
    const now = Date.now();
    const isCacheValid = cacheTimestamp && (now - parseInt(cacheTimestamp)) < CACHE_DURATION_MS;

    // If cache is valid and we have companies, skip reload
    if (isCacheValid && companies.length > 0) {
      console.log('✅ [FilterContext] Using valid cached companys for user:', userId);
      if (!hasInitialLoad) {
        // Auto-select all companies from cache if no selection cached
        const cachedSelection = getCachedData<string[]>(CACHE_KEY_BASE.selectedCompanyIds, userId);
        if (!cachedSelection) {
          const allCompanyIds = companies.map(company => company.id);
          setSelectedCompanyIds(allCompanyIds);
          setCachedData(CACHE_KEY_BASE.selectedCompanyIds, userId, allCompanyIds);
        }
        // Also set industry types to only industries with companies (validated against IndustryType)
        // CRITICAL: Validate cached industry types against available company industries
        const cachedIndustryTypesSelection = getCachedData<IndustryType[]>('company_filter_selected_industry_types', userId);
        validateAndRestoreIndustryTypes(
          cachedIndustryTypesSelection,
          companies,
          setSelectedIndustryTypes,
          (types) => setCachedData('company_filter_selected_industry_types', userId, types),
          '[FilterContext:cache-valid]'
        );
        setHasInitialLoad(true);
      }
      return;
    }

    /**
     * Applied to BOTH the first payload and any later background revalidation, so
     * a company that appears while the page is open lands without a reload.
     */
    function applyCompanies(data: Company[]) {
      if (!data) return;

      // Read the previous list BEFORE overwriting it — that is what tells us which
      // companies are new to this user and therefore must be auto-selected.
      const previousIds = (getCachedData<Company[]>(CACHE_KEY_BASE.companies, userId) || []).map(c => c.id);
      const allCompanyIds = data.map(company => company.id);

      setCompanies(data);
      // Cache the data with user-specific key
      setCachedData(CACHE_KEY_BASE.companies, userId, data);
      setCachedData(CACHE_KEY_BASE.timestamp, userId, Date.now().toString());

      // CRITICAL: Check localStorage directly to avoid race condition with cache load effect
      const cachedCompanySelection = getCachedData<string[]>(CACHE_KEY_BASE.selectedCompanyIds, userId);
      const merged = cachedCompanySelection && cachedCompanySelection.length > 0
        ? mergeSelectionWithNewEntities(cachedCompanySelection, previousIds, allCompanyIds)
        : allCompanyIds;
      // Nothing survived the merge (every cached id is gone) — fall back to all.
      const nextSelection = merged.length > 0 ? merged : allCompanyIds;

      setSelectedCompanyIds(nextSelection);
      setCachedData(CACHE_KEY_BASE.selectedCompanyIds, userId, nextSelection);
      console.log('✅ [FilterContext] Company selection:', nextSelection.length, 'of', allCompanyIds.length);

      // Auto-select only industries that have companies (validated against IndustryType)
      // CRITICAL: Validate cached industry types against available company industries
      // If user's companies have changed, cached selection might not include any valid industries
      const cachedIndustryTypesSelection = getCachedData<IndustryType[]>('company_filter_selected_industry_types', userId);
      validateAndRestoreIndustryTypes(
        cachedIndustryTypesSelection,
        data,
        setSelectedIndustryTypes,
        (types) => setCachedData('company_filter_selected_industry_types', userId, types),
        '[FilterContext:fresh-load]'
      );

      setHasInitialLoad(true);
      console.log('✅ [FilterContext] Cached', data.length, 'companies for user:', userId);
    }

    async function loadCompanies() {
      console.log('📡 [FilterContext] Cache stale or empty, loading companies for user:', userId);
      try {
        applyCompanies(await getCompanies({ onRefresh: applyCompanies }));
      } catch (error) {
        console.error('❌ [FilterContext] Failed to load companies:', error);
      }
    }
    loadCompanies();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- companies is depended on by .length only (re-fetch on count change, not content); same intentional exclusion as the groups loader below
  }, [rbacReady, user?.id, hasInitialLoad, companies.length, refreshNonce, setCompanies, setSelectedCompanyIds, setSelectedIndustryTypes, setHasInitialLoad]);

  // Fetch groups when selected companies change and auto-select all
  // CRITICAL: Must wait for rbacReady to prevent unauthorized data exposure
  useEffect(() => {
    console.log('🔍 [FilterContext] Groups useEffect triggered, selectedCompanyIds:', selectedCompanyIds);

    // CRITICAL: Wait for RBAC to be ready
    if (!rbacReady) {
      console.log('⏳ [FilterContext] Groups: waiting for rbacReady');
      return;
    }

    // Guard: Require authenticated user
    if (!user?.id) {
      console.log('⚠️ [FilterContext] Groups: no user, clearing groups');
      setGroups([]);
      setSelectedGroupIds([]);
      return;
    }

    const userId = user.id;

    async function loadGroups() {
      if (selectedCompanyIds.length === 0) {
        console.log('⚠️ [FilterContext] selectedCompanyIds is empty, skipping groups fetch');
        setGroups([]);
        setSelectedGroupIds([]);
        return;
      }

      // CRITICAL: Validate selectedCompanyIds against actual companies to prevent stale cache errors
      // This catches phantom company IDs that might be cached from previous sessions
      const validCompanyIds = new Set(companies.map(a => a.id));
      const validSelectedCompanyIds = selectedCompanyIds.filter(id => validCompanyIds.has(id));

      if (validSelectedCompanyIds.length !== selectedCompanyIds.length) {
        const invalidIds = selectedCompanyIds.filter(id => !validCompanyIds.has(id));
        console.warn('⚠️ [FilterContext] Removing invalid/stale company IDs from selection:', invalidIds);
        // Update state with only valid IDs to prevent repeated errors
        setSelectedCompanyIds(validSelectedCompanyIds);
        if (user?.id) {
          setCachedData(CACHE_KEY_BASE.selectedCompanyIds, user.id, validSelectedCompanyIds);
        }
        // If we removed all IDs, just return - the next effect will run with corrected IDs
        if (validSelectedCompanyIds.length === 0) {
          console.log('⚠️ [FilterContext] All selected company IDs were invalid, waiting for correction');
          return;
        }
      }

      // Build company name lookup for readable logs
      const companyNameMap = new Map(companies.map(c => [c.id, c.name]));
      const companyNames = validSelectedCompanyIds.map(id => companyNameMap.get(id) || id).join(', ');
      console.log('📡 [FilterContext] Fetching groups for companies:', companyNames);

      /**
       * Applied to BOTH the first payload and any later background revalidation.
       * Selecting every group each time is deliberate and unchanged: the group
       * filter is a full allow-list, so a production created after this device
       * last synced would otherwise leave its units off the map entirely.
       */
      function applyGroups(fetched: Group[]) {
        const uniqueGroups = Array.from(new Map(fetched.map(g => [g.id, g])).values());
        console.log('✅ [FilterContext] Loaded groups:', uniqueGroups.length, 'total');

        setGroups(uniqueGroups);
        // Cache the groups with user-specific key
        setCachedData(CACHE_KEY_BASE.groups, userId, uniqueGroups);

        // Auto-select all groups by default + unassigned for each company
        const groupIds = uniqueGroups.map(group => group.id);
        const unassignedIds = validSelectedCompanyIds.map(companyId => `__unassigned_${companyId}__`);
        const allGroupIds = [...groupIds, ...unassignedIds];
        setSelectedGroupIds(allGroupIds);
        setCachedData(CACHE_KEY_BASE.selectedGroupIds, userId, allGroupIds);
      }

      try {
        // Load groups for all selected companies in a single bulk request
        // This avoids N+1 API calls when multiple companies are selected
        console.log('📡 [FilterContext] Bulk fetching groups for', validSelectedCompanyIds.length, 'companies');
        let allGroups: Group[] = [];
        try {
          allGroups = await getBulkGroupsForCompanies(validSelectedCompanyIds, { onRefresh: applyGroups });
        } catch (bulkError) {
          console.warn('⚠️ [FilterContext] Bulk groups fetch failed, falling back to individual fetches:', bulkError);
          // Fallback to individual fetches if bulk fails (backward compatibility)
          const groupPromises = validSelectedCompanyIds.map(async (id) => {
            const companyName = companyNameMap.get(id);
            try {
              return await getGroupsForCompany(id, companyName);
            } catch (error) {
              console.warn(`⚠️ [FilterContext] Failed to fetch groups for company ${companyName || id}:`, error);
              return [];
            }
          });
          const groupArrays = await Promise.all(groupPromises);
          allGroups = groupArrays.flat();
        }

        applyGroups(allGroups);
      } catch (error) {
        console.error('❌ [FilterContext] Failed to load groups:', error);
        // Keep existing cached data on error - don't clear state
      }
    }
    loadGroups();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- companies used for validation, not to trigger re-fetch
  }, [selectedCompanyIds, rbacReady, user?.id, companies.length, refreshNonce]);

  // Load units for filter components (counts, available options)
  // PERFORMANCE: units restored from localStorage on mount already painted the
  // badge counts; this asks the API for the truth on every mount.
  //
  // It used to skip the request entirely whenever that localStorage mirror was
  // under an hour old. Combined with the unit list's own cache TTL, a unit moved
  // to a different production in Settings kept showing its OLD production in the
  // filter sidebar and on Reports long after the move. Freshness is now owned in
  // one place (src/lib/cache.ts), which dedupes and throttles the actual network
  // call, so asking here every mount is cheap.
  const unitsLoadedForRef = useRef<string | null>(null);
  useEffect(() => {
    // Only load if we have companies and RBAC is ready
    if (!hasInitialLoad || !rbacReady || !user?.id) return;

    const userId = user.id;
    // One request per mount per refresh cycle. `units` deliberately stays out of
    // the dependency list: applying a payload changes it, and re-running on that
    // would loop.
    const loadToken = `${userId}:${refreshNonce}`;
    if (unitsLoadedForRef.current === loadToken) return;
    unitsLoadedForRef.current = loadToken;

    function applyUnits(data: Unit[]) {
      console.log(`✅ [FilterContext] Loaded ${data.length} units for filters`);
      // Use setUnitsInternal directly - the exposed setUnits already wraps with startTransition
      setUnitsInternal(data);

      // Cache the fresh units data
      setCachedData(CACHE_KEY_BASE.units, userId, data);
      setCachedData(CACHE_KEY_BASE.unitsTimestamp, userId, Date.now());
    }

    async function loadUnitsData() {
      console.log('📡 [FilterContext] Loading units for filter components...');
      try {
        applyUnits(await getUnits(undefined, { onRefresh: applyUnits }));
      } catch (error) {
        console.error('❌ [FilterContext] Failed to load units:', error);
        // Keep existing cached data on error - don't clear state
      }
    }
    loadUnitsData();
  }, [hasInitialLoad, rbacReady, user?.id, refreshNonce, setUnitsInternal]);
}
